import MarkdownIt, { type MarkdownIt as MarkdownItInstance } from 'markdown-it'
import markdownItMermaid from './markdownItMermaid'
import markdownItTextMath from 'markdown-it-texmath'
import katex from "katex"
import "katex/contrib/mhchem"
import markdownItHighlightjs from 'markdown-it-highlightjs/core'
import hljs from 'highlight.js/lib/core'
import bash from 'highlight.js/lib/languages/bash'
import c from 'highlight.js/lib/languages/c'
import cpp from 'highlight.js/lib/languages/cpp'
import csharp from 'highlight.js/lib/languages/csharp'
import css from 'highlight.js/lib/languages/css'
import diff from 'highlight.js/lib/languages/diff'
import dockerfile from 'highlight.js/lib/languages/dockerfile'
import go from 'highlight.js/lib/languages/go'
import haskell from 'highlight.js/lib/languages/haskell'
import java from 'highlight.js/lib/languages/java'
import javascript from 'highlight.js/lib/languages/javascript'
import json from 'highlight.js/lib/languages/json'
import julia from 'highlight.js/lib/languages/julia'
import kotlin from 'highlight.js/lib/languages/kotlin'
import markdownLanguage from 'highlight.js/lib/languages/markdown'
import matlab from 'highlight.js/lib/languages/matlab'
import ocaml from 'highlight.js/lib/languages/ocaml'
import php from 'highlight.js/lib/languages/php'
import plaintext from 'highlight.js/lib/languages/plaintext'
import python from 'highlight.js/lib/languages/python'
import r from 'highlight.js/lib/languages/r'
import ruby from 'highlight.js/lib/languages/ruby'
import rust from 'highlight.js/lib/languages/rust'
import scala from 'highlight.js/lib/languages/scala'
import scheme from 'highlight.js/lib/languages/scheme'
import shell from 'highlight.js/lib/languages/shell'
import sql from 'highlight.js/lib/languages/sql'
import swift from 'highlight.js/lib/languages/swift'
import typescript from 'highlight.js/lib/languages/typescript'
import x86asm from 'highlight.js/lib/languages/x86asm'
import xml from 'highlight.js/lib/languages/xml'
import yaml from 'highlight.js/lib/languages/yaml'
import type { Source } from '@/services/types'
import {
  citationIdCharacterClass,
  createCitationPlugin,
  CitationProviderImpl,
  type MarkdownCitation,
} from './markdownCitations'

type MarkdownPlugin = (md: MarkdownItInstance) => void
type MarkdownPluginWithOptions<TOptions> = (md: MarkdownItInstance, options: TOptions) => void

function safePlugin(plugin: MarkdownPlugin): MarkdownPlugin
function safePlugin<TOptions>(plugin: MarkdownPluginWithOptions<TOptions>, options: TOptions): MarkdownPlugin
function safePlugin(plugin: MarkdownPlugin | MarkdownPluginWithOptions<unknown>, options?: unknown): MarkdownPlugin {
  return (md: MarkdownItInstance) => {
    try {
      if (options !== undefined) {
        (plugin as MarkdownPluginWithOptions<unknown>)(md, options)
      } else {
        (plugin as MarkdownPlugin)(md)
      }
    } catch {
      // Plugin registration failed, continue without it
    }
  }
}

// Grammars for the languages students and staff regularly paste or receive:
// course languages (Java, C/C++, R, MATLAB, OCaml, ...) plus web, backend, and
// operations formats. Unregistered languages are escaped as plain code.
const highlightLanguages = {
  bash,
  c,
  cpp,
  csharp,
  css,
  diff,
  dockerfile,
  go,
  haskell,
  java,
  javascript,
  json,
  julia,
  kotlin,
  markdown: markdownLanguage,
  matlab,
  ocaml,
  php,
  plaintext,
  python,
  r,
  ruby,
  rust,
  scala,
  scheme,
  shell,
  sql,
  swift,
  typescript,
  x86asm,
  xml,
  yaml,
}

const texmath = markdownItTextMath as typeof markdownItTextMath & {
  rules: Record<string, {
    inline: Array<Record<string, unknown>>
    block?: Array<Record<string, unknown>>
  }>
}

const bracketsRules = texmath.rules.brackets!
const gitlabRules = texmath.rules.gitlab!
// Add a rule to allow block brackets "inline" as in a list
texmath.rules.brackets_plus = {
  ...bracketsRules,
  inline: [
    {
      name: 'math_inline_brackets_display',
      rex: /\\\[([\s\S]+?)\\\]/gy,
      tmpl: '<section><eqn>$1</eqn></section>',
      tag: '\\[',
      displayMode: true,
    },
    ...bracketsRules.inline,
  ],
}
texmath.rules.gitlab = {
  ...gitlabRules,
  block: [
    {
      name: 'math_block_eqno',
      rex: /`{3}(?:math|latex|tex)\s*([^`]+?)\s*?`{3}\s*\(([^)\r\n]+?)\)/gm,
      tmpl: '<section class="eqno"><eqn>$1</eqn><span>($2)</span></section>',
      tag: '```',
    },
    {
      name: 'math_block',
      rex: /`{3}(?:math|latex|tex)\s*([^`]*?)\s*`{3}/gm,
      tmpl: '<section><eqn>$1</eqn></section>',
      tag: '```',
    },
  ],
}

export const markdown = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: true,
})
markdown.linkify.set({ fuzzyLink: true })

markdown.use(safePlugin(texmath, {
  engine: katex,
  delimiters: ['dollars', 'brackets_plus', 'gitlab', 'beg_end'],
}))
// The core entry point has no defaults of its own; these match the full
// `markdown-it-highlightjs` entry point: auto-detect unlabeled fences,
// highlight indented code blocks, and tolerate grammar-illegal input.
markdown.use(safePlugin(markdownItHighlightjs, {
  hljs,
  register: highlightLanguages,
  auto: true,
  code: true,
  ignoreIllegals: true,
}))
markdown.use(safePlugin(markdownItMermaid))
markdown.use(createCitationPlugin())

const originalLinkOpen = markdown.renderer.rules.link_open
markdown.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx]
  const href = String(token?.attrGet('href') ?? '')

  if (token && href && !href.startsWith('#')) {
    token.attrSet('target', '_blank')
    token.attrSet('rel', 'noopener noreferrer')
  }

  if (originalLinkOpen) {
    return originalLinkOpen(tokens, idx, options, env, self)
  }
  return self.renderToken(tokens, idx, options)
}

// Wrap fence renderer to catch any highlighting errors
const originalFence = markdown.renderer.rules.fence
if (originalFence) {
  markdown.renderer.rules.fence = (tokens, idx, options, env, self) => {
    try {
      return originalFence(tokens, idx, options, env, self)
    } catch {
      const token = tokens[idx]
      const escaped = (token?.content || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return `<pre><code>${escaped}</code></pre>`
    }
  }
}

export interface RenderMarkdownOptions {
  sources?: Source[]
  footnotePrefix?: string
}

export interface RenderedMarkdown {
  html: string
  citations: MarkdownCitation[]
}

function normalizeCitationSyntax(text: string): string {
  // Remove trailing spaces before citation references (prevents soft breaks).
  // Exclude footnote definitions [^id]:
  const withoutCitationSoftBreaks = text.replace(
    new RegExp(`[ \\t]+\\n(\\[\\^(${citationIdCharacterClass})\\])(?!:)`, 'g'),
    '$1',
  )

  // Ensure linkify doesn't absorb trailing [^citation] into the URL token.
  return withoutCitationSoftBreaks.replace(
    /((?:https?:\/\/|www\.)[^\s<]+?)(\[\^[^\]\s]+\](?:[ \t]*\[\^[^\]\s]+\])*)(?=(?:\s|$))/g,
    '$1\u00A0$2',
  )
}

export function renderMarkdown(input: string, options: RenderMarkdownOptions = {}): RenderedMarkdown {
  const cleanedInput = normalizeCitationSyntax(input)
  const provider = new CitationProviderImpl({
    sources: options.sources,
    footnotePrefix: options.footnotePrefix,
    escapeHtml: markdown.utils.escapeHtml,
  })

  const env = {
    citationContext: {
      provider,
    },
  }

  try {
    const html = markdown.render(cleanedInput, env)
    return {
      html,
      citations: provider.getCitations(),
    }
  } catch {
    // Last resort: escape and return as preformatted text
    const escaped = input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    return {
      html: `<pre>${escaped}</pre>`,
      citations: [],
    }
  }
}

export type { MarkdownCitation } from './markdownCitations'
